import { createSelector } from "@reduxjs/toolkit";
import { compose } from "ramda";

import { selectLoggedUser } from "../components/appData.slice";
import { selectFormData as selectACApplicantData } from "../features/AC/Applicant/applicant.selectors";
import { selectFormData as selectACIncidentData } from "../features/AC/Incident/incident.selectors";
import { selectFormData as selectACPerpetratorData } from "../features/AC/Perpetrator/perpetrator.selectors";
import { selectAgreeDecisionEmail } from "../features/AC/Summary/summary.selectors";
import { selectAgreeRentinVehicleEmail } from "../features/AC/Summary/summary.selectors";
import { selectAgreeContactEmail } from "../features/AC/Summary/summary.selectors";
import { selectFormDataWithoutRedirect as selectACVehicleData } from "../features/AC/Vehicle/vehicle.selectors";
import { selectVehicleDriveable } from "../features/AC/Vehicle/vehicle.selectors";
import { selectFormData as selectOCPropertyDamagesData } from "../features/OC/Property/Damages/damages.selectors";
import { selectFormData as selectOCPropertyIncidentData } from "../features/OC/Property/Incident/incident.selectors";
import { selectFormData as selectOCPropertyOwnerData } from "../features/OC/Property/Owner/owner.selectors";
import { selectFormDataWithoutRedirect as selectOCPropertyPerpetratorData } from "../features/OC/Property/Perpetrator/perpetrator.selectors";
import { selectFormData as selectOCVehicleDamages } from "../features/OC/Vehicle/Damages/damages.selectors";
import { selectFormData as selectOCVehicleIncidentData } from "../features/OC/Vehicle/Incident/incident.selectors";
import { selectFormData as selectOCVehicleIntroductoryData } from "../features/OC/Vehicle/Introductory/introductory.selectors";
import { selectFormData as selectOCVehicleVehicleOwnerData } from "../features/OC/Vehicle/VehicleOwner/vehicleOwner.selectors";
import { defaultCountry } from "../shared";
import { generatePhone } from "../utilities/forms";

import { Payload } from "./types";

type NormalizeFn = (
  key: string,
  value: string,
  payload: Payload,
  oldPayload: Payload
) => Payload;

const normalizePayload = (state: Payload, normalizeFn: NormalizeFn) => {
  let newPayload: Payload = {};

  for (const [key, value] of Object.entries(state)) {
    if (typeof value !== "object" && typeof value !== "undefined") {
      newPayload = normalizeFn(
        key,
        value,
        JSON.parse(JSON.stringify(newPayload)),
        state
      );
    }

    if (typeof value === "object") {
      newPayload[key] = Array.isArray(value)
        ? value.map((newValue) => ({
            ...normalizePayload(newValue as Payload, normalizeFn),
          }))
        : {
            ...normalizePayload(value as Payload, normalizeFn),
          };
    }
  }

  return newPayload;
};

const normalizeProvinceCode = (state: Payload) => {
  const normalizeFn = (
    key: string,
    value: string,
    newPayload: Payload,
    _: Payload
  ) => {
    if (["zipCodePL", "zipCodeGeneric"].includes(key)) {
      newPayload["zipCode"] = !newPayload["zipCode"]
        ? value
        : newPayload["zipCode"] !== "" && !value
        ? newPayload["zipCode"]
        : value;
    } else {
      newPayload[key] = value;
    }

    return JSON.parse(JSON.stringify(newPayload));
  };

  return normalizePayload(state, normalizeFn);
};

const normalizePhone = (state: Payload) => {
  const normalizeFn = (
    key: string,
    value: string,
    newPayload: Payload,
    _: Payload
  ) => {
    if (key === "phone") {
      newPayload[key] = generatePhone(value);
    } else {
      newPayload[key] = value;
    }

    return JSON.parse(JSON.stringify(newPayload));
  };

  return normalizePayload(state, normalizeFn);
};

const normalizeProvince = (state: Payload) => {
  const normalizeFn = (
    key: string,
    value: string,
    newPayload: Payload,
    oldState: Payload
  ) => {
    newPayload[key] = value;

    if (key === "province" && oldState?.country) {
      if (
        (oldState.country as Record<string, string>).label !==
        defaultCountry.label
      ) {
        newPayload[key] = "";
      }
    }

    return JSON.parse(JSON.stringify(newPayload));
  };

  return normalizePayload(state, normalizeFn);
};

export const selectACFormData = createSelector(
  selectLoggedUser,
  selectVehicleDriveable,
  selectAgreeDecisionEmail,
  selectAgreeRentinVehicleEmail,
  selectAgreeContactEmail,
  selectACVehicleData,
  selectACApplicantData,
  selectACIncidentData,
  selectACPerpetratorData,
  (
    loggedUser,
    vehicleDriveable,
    AgreeDecisionEmail,
    AgreeRentinVehicleEmail,
    AgreeContactEmail,
    vehicle,
    applicant,
    incident,
    perpetrator
  ) => ({
    loggedUser,
    vehicleDriveable,
    AgreeDecisionEmail,
    AgreeRentinVehicleEmail,
    AgreeContactEmail,
    vehicle: compose(normalizeProvince)(vehicle),
    applicant: compose(
      normalizeProvince,
      normalizeProvinceCode,
      normalizePhone
    )(applicant),
    incident: compose(normalizeProvince, normalizeProvinceCode)(incident),
    perpetrator: compose(
      normalizeProvince,
      normalizeProvinceCode,
      normalizePhone
    )(perpetrator),
  })
);

export const selectOCVehicleFormData = createSelector(
  selectLoggedUser,
  selectOCVehicleIntroductoryData,
  selectOCVehicleIncidentData,
  selectOCVehicleVehicleOwnerData,
  selectOCVehicleDamages,
  (loggedUser, introductory, incident, vehicleOwner, damages) => ({
    loggedUser,
    introductory: compose(normalizePhone)(introductory),
    incident: compose(
      normalizeProvince,
      normalizeProvinceCode,
      normalizePhone
    )(incident),
    vehicleOwner: compose(
      normalizeProvince,
      normalizeProvinceCode,
      normalizePhone
    )(vehicleOwner),
    damages,
  })
);

export const selectOCPropertyFormData = createSelector(
  selectOCPropertyPerpetratorData,
  selectOCPropertyIncidentData,
  selectOCPropertyOwnerData,
  selectOCPropertyDamagesData,
  (perpetrator, incident, owner, damages) => ({
    perpetrator,
    incident: compose(
      normalizeProvince,
      normalizeProvinceCode,
      normalizePhone
    )(incident),
    owner: compose(
      normalizeProvince,
      normalizeProvinceCode,
      normalizePhone
    )(owner),
    damages,
  })
);
